// components/MachineEdit.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

const MachineEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isNew = id === "new" || !id;

  const [machine, setMachine] = useState({
    name: "",
    material_id: "",
    process: "",
    type_id: "",
    manufacturer_id: "",
  });

  const [types, setTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [imageFile, setImageFile] = useState(null);
  const [specFile, setSpecFile] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedTechOptions, setSelectedTechOptions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
  const loadAll = async () => {
    try {
      const [materialsRes, typesRes, manufacturersRes, techRes] = await Promise.all([
        axios.get("https://modularobjects.com/checkout/api/materials"),
        axios.get("https://modularobjects.com/checkout/api/machine-types"),
        axios.get("https://modularobjects.com/checkout/api/manufacturers"),
        axios.get("https://modularobjects.com/checkout/api/technologies")
      ]);

      const materialOptions = materialsRes.data.map(m => ({
        value: m.id,
        label: m.name
      }));
      setMaterials(materialOptions);
      setTypes(typesRes.data);
      setManufacturers(manufacturersRes.data);
      setTechnologies(techRes.data.map(t => ({ value: t.id, label: t.name })));

      // Ako je edit mod, učitaj postojeću mašinu
      if (!isNew) {
        const machineRes = await axios.get(`https://modularobjects.com/checkout/api/machines/${id}`);
        const machineData = machineRes.data;
        setMachine(machineData);

        const selectedMat = materialOptions.find(m => m.value === machineData.material_id);
        setSelectedMaterial(selectedMat || null);

        const selectedTechs = techRes.data
          .filter(t => machineData.technology_ids?.includes(t.id))
          .map(t => ({ value: t.id, label: t.name }));
        setSelectedTechOptions(selectedTechs);
      } else {
        // Ako je nova mašina, inicijalizuj prazna polja
        setMachine({
          name: "",
          process: "",
          type_id: "",
          manufacturer_id: ""
        });
        setSelectedMaterial(null);
        setSelectedTechOptions([]);
      }
    } catch (err) {
      console.error("Greška pri učitavanju:", err);
    }
  };

  loadAll();
}, [id, isNew]);


 const handleSubmit = async (e) => {
  e.preventDefault();
  const formData = new FormData();
  formData.append("name", machine.name);
  formData.append("material_id", selectedMaterial?.value || "");
  formData.append("process", machine.process);
  formData.append("type_id", machine.type_id);
  formData.append("manufacturer_id", machine.manufacturer_id);
  if (imageFile) formData.append("image", imageFile);
  if (specFile) formData.append("spec", specFile);

  try {
    let machineId = id;
    if (isNew) {
      const res = await axios.post("https://modularobjects.com/checkout/api/machines", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      machineId = res.data.id; // <- dobijaš novi ID
    } else {
      await axios.put(`https://modularobjects.com/checkout/api/machines/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    }

    // Tehnologije
    await axios.post(`https://modularobjects.com/checkout/api/machines/${machineId}/technologies`, {
      technology_ids: selectedTechOptions.map((opt) => opt.value)
    });

    navigate("/machines");
  } catch (err) {
    console.error("Greška pri čuvanju:", err);
  }
};


  const handleDelete = async () => {
    if (!window.confirm("Da li ste sigurni da želite da obrišete ovu mašinu?")) return;

    try {
      await axios.delete(`https://modularobjects.com/checkout/api/machines/${id}`);
      navigate("/machines");
    } catch (err) {
      console.error("Greška pri brisanju:", err);
    }
  };

  if (!machine) return <p>Učitavanje...</p>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">{isNew ? "Dodavanje nove mašine" : "Izmena mašine"}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-semibold mb-1">Naziv mašine:</label>
          <input type="text" value={machine.name} onChange={(e) => setMachine({ ...machine, name: e.target.value })} className="border p-2 w-full" />
        </div>

        <div className="mb-4">
          <label className="block font-semibold mb-1">Materijal</label>
          <Select
            options={materials}
            value={selectedMaterial}
            onChange={setSelectedMaterial}
            placeholder="Izaberi materijal"
            isClearable
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Tip mašine:</label>
          <select value={machine.type_id} onChange={(e) => setMachine({ ...machine, type_id: e.target.value })} className="border p-2 w-full">
            <option value="">-- Tip --</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block font-semibold mb-1">Proizvođač:</label>
          <select value={machine.manufacturer_id} onChange={(e) => setMachine({ ...machine, manufacturer_id: e.target.value })} className="border p-2 w-full">
            <option value="">-- Proizvođač --</option>
            {manufacturers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block font-semibold mb-1">Tehnologije:</label>
          <Select isMulti value={selectedTechOptions} onChange={setSelectedTechOptions} options={technologies} placeholder="Izaberi tehnologije" />
        </div>

        <div>
          <label className="block font-semibold mb-1">Slika mašine:</label>
          <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className="border p-2 w-full" />
          {machine?.image_url && !isNew && (
            <div className="mt-2">
              <img
                src={`https://modularobjects.com/checkout/${machine.image_url.replace(/^\/+/,'')}`}
                className="max-h-48 rounded shadow"
                alt="Slika mašine"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block font-semibold mb-1">Specifikacija (PDF):</label>
          <input type="file" accept="application/pdf" onChange={(e) => setSpecFile(e.target.files[0])} className="border p-2 w-full" />
          {machine?.spec_url && !isNew ? (
            <div className="mt-2 space-x-4">
              <a href={`https://modularobjects.com/checkout/${machine.spec_url.replace(/^\/+/,'')}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">📄 Pogledaj</a>
              <a href={`https://modularobjects.com/checkout/${machine.spec_url.replace(/^\/+/,'')}`} download className="text-blue-600 underline">⬇️ Preuzmi</a>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-2">Nema trenutno dodate PDF specifikacije. Dodaj jednu iznad.</p>
          )}
        </div>

        <div className="flex justify-between items-center pt-4">
          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            💾 Sačuvaj {isNew ? "mašinu" : "izmene"}
          </button>

          {!isNew && (
            <button onClick={() => setShowDeleteModal(true)} type="button" className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700">
              🗑 Obriši mašinu
            </button>
          )}
        </div>
      </form>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Potvrda brisanja</h3>
            <p className="mb-4">Da li ste sigurni da želite da obrišete ovu mašinu?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">Otkaži</button>
              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">Obriši</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachineEdit;
